import React from "react";
import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import { attachBpmnTouchNavigation } from "../../utils/bpmnTouchNavigation";

/**
 * Read-only interactive BPMN canvas for the Process Optimization page.
 *
 * Renders BPMN XML in bpmn-js's NavigatedViewer — the same engine as the CMS BPMN
 * editor, minus editing — so every activity, gateway, event, arrow and link can be
 * inspected up close: scroll/drag to pan, buttons or mouse wheel to zoom, and click
 * an element to see its type and name. Editing stays in the CMS editor; diagrams
 * here are DERIVED (optimized/analyzed) and regenerate on each run.
 */
export default function BpmnInteractiveViewer({ xml, height = 480, emptyMessage, onViewerReady }) {
  const containerRef = React.useRef(null);
  const viewerRef = React.useRef(null);
  const detachTouchRef = React.useRef(null);
  const [status, setStatus] = React.useState("idle"); // idle | loading | ready | error
  const [error, setError] = React.useState("");
  const [selected, setSelected] = React.useState(null); // { type, name }

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!xml || !containerRef.current) return;
      setStatus("loading");
      setError("");
      // The XML changed — whatever was clicked belongs to the PREVIOUS diagram;
      // keeping it would identify an element that is no longer on screen.
      setSelected(null);
      try {
        // Lazy import keeps bpmn-js out of the main bundle until the viewer is used
        // (same pattern as the CMS BpmnEditor).
        const NavigatedViewer = (await import("bpmn-js/lib/NavigatedViewer")).default;
        if (cancelled) return;
        if (!viewerRef.current) {
          viewerRef.current = new NavigatedViewer({ container: containerRef.current });
          // Finger pan / pinch zoom, within the same limits as the zoom buttons.
          detachTouchRef.current = attachBpmnTouchNavigation(
            containerRef.current,
            viewerRef.current.get("canvas"),
            { minZoom: 0.2, maxZoom: 4 },
          );
          const eventBus = viewerRef.current.get("eventBus");
          eventBus.on("element.click", (e) => {
            const el = e.element;
            if (!el || el.type === "bpmn:Process" || el.type === "bpmn:Collaboration") {
              setSelected(null);
              return;
            }
            setSelected({
              type: String(el.type || "").replace("bpmn:", ""),
              name: el.businessObject?.name || el.id,
            });
          });
        }
        // Guard BEFORE the import too: if the xml prop changed while the lazy
        // import above resolved, this run must not race the newer effect's
        // importXML on the same shared viewer instance.
        if (cancelled) return;
        await viewerRef.current.importXML(xml);
        if (cancelled) return;
        // The import succeeded — the diagram IS on screen at this point. Auto-fit
        // is cosmetic on top of that, so its failure must never blank the diagram.
        // Reproduced live (deployed testing, process 1979, Current sub-tab):
        // "Failed to execute 'scale' on 'SVGMatrix': The provided float value is
        // non-finite." Verified NOT an XML/data problem (all 27 shape bounds in
        // the fetched diagram are finite, positive numbers) — it is bpmn-js's
        // documented fit-viewport race: computing the fit scale from the SVG's
        // getBBox() one paint tick too early (right after a second importXML on
        // an already-mounted viewer) can return a transient zero-size box, whose
        // reciprocal is Infinity/NaN. Deferring one frame lets the browser finish
        // painting the just-imported content first; the outer try/catch still
        // exists as a last-resort net but must not fire from this specific call.
        requestAnimationFrame(() => {
          if (cancelled || !viewerRef.current) return;
          try {
            viewerRef.current.get("canvas").zoom("fit-viewport", "auto");
          } catch (zoomErr) {
            // Cosmetic failure only — the diagram itself already rendered.
            console.warn("BPMN auto-fit failed (diagram still shown):", zoomErr);
          }
        });
        setStatus("ready");
        // Hand the mounted viewer up so a parent can drive it directly (e.g. the
        // token-walkthrough animator marking elements along a path) — only once
        // this xml is actually on screen, so the parent never acts on stale content.
        onViewerReady?.(viewerRef.current);
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setError(e?.message || "The diagram could not be loaded.");
        }
      }
    }
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xml]);

  React.useEffect(() => () => {
    detachTouchRef.current?.();
    detachTouchRef.current = null;
    if (viewerRef.current) {
      viewerRef.current.destroy();
      viewerRef.current = null;
      onViewerReady?.(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zoomBy = (delta) => {
    const canvas = viewerRef.current?.get("canvas");
    if (!canvas) return;
    try {
      canvas.zoom(Math.max(0.2, Math.min(4, canvas.zoom() + delta)));
    } catch (e) {
      console.warn("BPMN zoom failed:", e);
    }
  };
  const zoomFit = () => {
    try {
      viewerRef.current?.get("canvas")?.zoom("fit-viewport", "auto");
    } catch (e) {
      console.warn("BPMN fit-viewport failed:", e);
    }
  };

  // The container node stays mounted at all times — even while `xml` is null
  // between diagram tabs (e.g. Optimized → Current, whose XML is still fetching).
  // If it were swapped out for an empty-state element, the persisted bpmn-js
  // viewer would stay bound to the removed node, and the next importXML would
  // render into nothing (blank canvas until a Static-image round-trip forced a
  // fresh viewer). Keeping the node stable lets the single viewer instance
  // re-import onto the same element every time.
  return (
    <div>
      {xml && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <button type="button" className="secondary-btn sm" onClick={() => zoomBy(0.15)} aria-label="Zoom in on the diagram">＋ Zoom in</button>
          <button type="button" className="secondary-btn sm" onClick={() => zoomBy(-0.15)} aria-label="Zoom out of the diagram">− Zoom out</button>
          <button type="button" className="secondary-btn sm" onClick={zoomFit} aria-label="Fit the whole diagram in view">⤢ Fit</button>
          <span style={{ fontSize: 11.5, color: "#9ca3af" }}>Drag to pan · scroll or pinch to zoom · click or tap an element to identify it</span>
          {selected && (
            <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "#0e6b68", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 999, padding: "2px 10px" }}>
              {selected.type}: {selected.name}
            </span>
          )}
        </div>
      )}
      <div style={{ position: "relative", border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", overflow: "hidden" }}>
        {status === "loading" && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.7)", zIndex: 2, fontSize: 13, color: "#6b7280" }}>
            Loading interactive diagram…
          </div>
        )}
        {status === "error" && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 30, textAlign: "center", background: "#fff", zIndex: 2, color: "#b91c1c", fontSize: 13 }}>{error}</div>
        )}
        {!xml && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 40, textAlign: "center", background: "#fff", zIndex: 2, color: "#6b7280", fontSize: 13 }}>
            {emptyMessage || "No diagram data available yet."}
          </div>
        )}
        <div ref={containerRef} className="bpmn-token-anim-host" style={{ height, width: "100%", touchAction: "none" }} />
      </div>
    </div>
  );
}
